import {
  BayLevelEnum,
  IBayLevelData,
  ILidData,
  IShipData,
  ISizeSummary,
} from "open-vessel-definition";
import { CONTAINER_HEIGHT_IN_MMM, TCG_IN_MMM } from "../consts";
import {
  IAftBayLayout,
  IAftBayRowInput,
  calculateAftBayLayout,
} from "../aftView/calculateAftBayLayout";
import { IBayPattern, IRowPattern, ISlotPattern, ITierPattern } from "../types/IPositionPatterns";
import { ITiersByRow, getRowsAndTiersFromSlotKeys } from "../getRowsAndTiersFromSlotKeys";
import { pad2, roundDec, sortNumericAsc } from "@tedivo/tedivo-pure-helpers";

import { bayLevelHasCenterLineRow } from "../bayLevelHasCenterLineRow";
import { calculateTeusFromSlots } from "../calculateTeusFromSlots";
import { getAllThePairedBays } from "../getAllThePairedBays";
import { getIsoBaysUnderLid } from "../getIsoBaysUnderLid";
import { sortRowsArray } from "../sortRowsArray";

export interface IVesselParticularsSectionLevel {
  level: BayLevelEnum;
  layout: IAftBayLayout;
  tiersByRow: ITiersByRow;
  /** False when any TCG was missing and the row positions were synthesized (do not display them) */
  tcgsAreReal: boolean;
  /** False when any bottom base was missing and the stack bases were synthesized (do not display them) */
  bottomBasesAreReal: boolean;
  /** Tier -> bottom base in mm (most common value across the rows), sorted bottom-up */
  tierBottomBases: Array<{ tier: ITierPattern; bottomBaseMm: number }>;
}

/** One drawn cross-section of the plan. Bays with identical geometry share a section. */
export interface IVesselParticularsSection {
  /** The bays this section represents (e.g. ["005", "017"]), ascending */
  isoBays: IBayPattern[];
  above?: IVesselParticularsSectionLevel;
  below?: IVesselParticularsSectionLevel;
  /** Union of the rows of both levels, Port to Starboard */
  rows: IRowPattern[];
  /** Horizontal position in mm per row (real TCG, or synthesized when the file has none) */
  rowXsMm: Partial<Record<IRowPattern, number>>;
  /** TCG in mm per row for the header strip; only rows whose level has real TCGs */
  rowTcgsMm: Partial<Record<IRowPattern, number>>;
  /** Horizontal extent of the section, in raw TCG mm (0 = centerline) */
  xMinMm: number;
  xMaxMm: number;
  /** Vertical extent in absolute VCG mm, from lowest bottom base to highest stack top */
  minVcgMm: number;
  maxVcgMm: number;
  /** The hatch-cover lids lying over this section's bays (every bay of the group has the same ones) */
  lids: ILidData[];
}

export interface IVesselParticularsBayCounts {
  isoBay: IBayPattern;
  count20Above: number;
  count20Below: number;
  lcg20AboveMm?: number;
  lcg20BelowMm?: number;
  /** Present on the AFT bay of a pair only, so each pair is counted once */
  pairedWith?: IBayPattern;
  count40Above?: number;
  count40Below?: number;
  lcg40Mm?: number;
}

export interface IVesselMainParticulars {
  shipName?: string;
  lineOperator?: string;
  callSign?: string;
  imoCode?: string;
  yearBuilt?: number;
  loaMm?: number;
  lppMm?: number;
  teuOnDeck: number;
  teuInHold: number;
  teuTotal: number;
}

export interface IVesselParticularsData {
  sections: IVesselParticularsSection[];
  /** One entry per ISO bay present in the file, ascending (fwd bays have higher labels drawn first by the composer) */
  bayCounts: IVesselParticularsBayCounts[];
  particulars: IVesselMainParticulars;
}

interface IBuildVesselParticularsDataProps {
  shipData: IShipData;
  sizeSummary: ISizeSummary;
  baysData: IBayLevelData[];
  lidData?: ILidData[];
}

/**
 * Derives everything a vessel-particulars sheet needs from an OVD
 * file: one cross-section per group of geometrically identical bays (real TCGs and
 * bottom bases via `calculateAftBayLayout`, synthesized and flagged when the file
 * has none), 20'/40' slot counts and LCGs per bay, and the ship particulars.
 * Pure data — see tedivo-bay-grid-core for the drawing.
 */
export function buildVesselParticularsData({
  shipData,
  sizeSummary,
  baysData,
  lidData,
}: IBuildVesselParticularsDataProps): IVesselParticularsData {
  const sections = buildSections(shipData, sizeSummary, baysData, lidData);
  const bayCounts = buildBayCounts(baysData);
  const particulars = buildParticulars(shipData, baysData);

  return { sections, bayCounts, particulars };
}

/** Synthetic bottom base of the vessel-wide lowest hold tier when the file has no VCGs (only for drawing) */
const SYNTHETIC_BELOW_BASE_MM = 2000;
/** Synthetic bottom base of the vessel-wide lowest deck tier when the file has no VCGs and no hold at all */
const SYNTHETIC_ABOVE_BASE_MM = 16000;
/** Vertical allowance between the top of the hold stacks and the deck bottom base (hatch covers) */
const SYNTHETIC_HATCH_CLEARANCE_MM = 1000;

/**
 * The anchor of every synthesized bottom base of one level kind: the vessel-wide
 * lowest ISO tier of the kind and the base it synthesizes to
 */
interface ISyntheticAnchor {
  refTier: number;
  baseMm: number;
}

/** The usable slot keys of a bay-level: not restricted, and no leftover 00 slots on a level without the centerline row (same as the Bay views, see bayLevelHasCenterLineRow) */
function usableSlotKeys(bl: IBayLevelData, sizeSummary: ISizeSummary): ISlotPattern[] {
  const hasCenterLineRow = bayLevelHasCenterLineRow(sizeSummary, bl);

  return (Object.keys(bl.perSlotInfo ?? {}) as ISlotPattern[]).filter(
    (key) => !bl.perSlotInfo?.[key]?.restricted && (hasCenterLineRow || !key.startsWith("00")),
  );
}

/** The real bottom base of a row, resolved like the Bay views: per row, then common, then the master base of the row's lowest tier */
function resolveRowBottomBase(
  bl: IBayLevelData,
  row: IRowPattern,
  tiersByRow: ITiersByRow,
  masterCGs: IShipData["masterCGs"],
): number | undefined {
  const bottomTier = tiersByRow[row] ? pad2(tiersByRow[row].minTier) : undefined;

  return (
    bl.perRowInfo?.each?.[row]?.bottomBase ??
    bl.perRowInfo?.common?.bottomBase ??
    (bottomTier !== undefined ? masterCGs.bottomBases[bottomTier] : undefined)
  );
}

/**
 * Builds the per-level layout data of one bay-level, or undefined when it has no
 * usable slots. Missing CGs never drop the bay: row positions and stack bases are
 * synthesized (uniform 2520 mm pitch, stacked container heights) and flagged as
 * not real, so the drawing shows the geometry but omits the CG numbers. The
 * synthesized bases are anchored on the vessel-wide lowest tier of the level
 * kind, so the same tier lands at the same height in every bay and the sections
 * of a CG-less vessel align perfectly on the sheet.
 */
function buildSectionLevel(
  bl: IBayLevelData,
  masterCGs: IShipData["masterCGs"],
  sizeSummary: ISizeSummary,
  synthAnchor: ISyntheticAnchor,
): IVesselParticularsSectionLevel | undefined {
  const slotKeys = usableSlotKeys(bl, sizeSummary);
  if (slotKeys.length === 0) return undefined;

  const { rows, tiersByRow } = getRowsAndTiersFromSlotKeys(slotKeys);

  const masterTcgs = bl.level === BayLevelEnum.ABOVE ? masterCGs.aboveTcgs : masterCGs.belowTcgs;
  const each = bl.perRowInfo?.each;
  const common = bl.perRowInfo?.common;

  const perRow: Partial<Record<IRowPattern, IAftBayRowInput>> = {};

  rows.forEach((row) => {
    const rowInfo = each?.[row];

    perRow[row] = {
      tcg: rowInfo?.tcg ?? masterTcgs[row],
      bottomBase: resolveRowBottomBase(bl, row, tiersByRow, masterCGs),
      maxHeight: rowInfo?.maxHeight ?? common?.maxHeight,
    };
  });

  // A single missing value makes the whole level synthetic: mixing real and
  // synthesized CGs would distort the drawn geometry.
  const tcgsAreReal = rows.every((row) => perRow[row]?.tcg !== undefined);
  if (!tcgsAreReal) {
    rows.forEach((row, idx) => {
      (perRow[row] as IAftBayRowInput).tcg = (idx - (rows.length - 1) / 2) * TCG_IN_MMM;
    });
  }

  const bottomBasesAreReal = rows.every((row) => perRow[row]?.bottomBase !== undefined);
  if (!bottomBasesAreReal) {
    // Anchored on the VESSEL-wide lowest tier (not this bay's), so tier N
    // synthesizes to the same height in every bay
    const localMinTier = Math.min(...rows.map((row) => tiersByRow[row]?.minTier ?? Infinity));
    const refTier = Number.isFinite(synthAnchor.refTier) ? synthAnchor.refTier : localMinTier;
    rows.forEach((row) => {
      const minTier = tiersByRow[row]?.minTier ?? refTier;
      (perRow[row] as IAftBayRowInput).bottomBase =
        synthAnchor.baseMm + ((minTier - refTier) / 2) * CONTAINER_HEIGHT_IN_MMM;
    });
  }

  const getTierHeightMm = (row: IRowPattern, tierIndex: number): number => {
    const rowTiers = tiersByRow[row];
    if (!rowTiers) return CONTAINER_HEIGHT_IN_MMM;
    const isoTier = pad2(rowTiers.minTier + tierIndex * 2);
    return (
      bl.perRowInfo?.each?.[row]?.minTierHeights?.[isoTier] ??
      common?.minTierHeights?.[isoTier] ??
      CONTAINER_HEIGHT_IN_MMM
    );
  };

  const layout = calculateAftBayLayout({ rows, tiersByRow, perRow, getTierHeightMm });
  if (!layout || layout.rows.length === 0) return undefined;

  return {
    level: bl.level,
    layout,
    tiersByRow,
    tcgsAreReal,
    bottomBasesAreReal,
    tierBottomBases: calculateTierBottomBases(layout),
  };
}

/** One bottom base per ISO tier: the most common value across the rows that have the tier */
function calculateTierBottomBases(
  layout: IAftBayLayout,
): Array<{ tier: ITierPattern; bottomBaseMm: number }> {
  const basesByTier = new Map<number, number[]>();

  layout.rows.forEach((row) => {
    row.tierBasesMm.forEach((baseMm, tierIndex) => {
      const isoTier = Number(row.bottomTier) + tierIndex * 2;
      const base = roundDec(baseMm, 0);
      const list = basesByTier.get(isoTier);
      if (list) list.push(base);
      else basesByTier.set(isoTier, [base]);
    });
  });

  return Array.from(basesByTier.keys())
    .sort(sortNumericAsc)
    .map((isoTier) => ({
      tier: pad2(isoTier) as ITierPattern,
      bottomBaseMm: modeOf(basesByTier.get(isoTier) as number[]),
    }));
}

function modeOf(values: number[]): number {
  const counts = new Map<number, number>();
  let best = values[0];
  let bestCount = 0;
  values.forEach((v) => {
    const c = (counts.get(v) ?? 0) + 1;
    counts.set(v, c);
    if (c > bestCount) {
      best = v;
      bestCount = c;
    }
  });
  return best;
}

function buildSections(
  shipData: IShipData,
  sizeSummary: ISizeSummary,
  baysData: IBayLevelData[],
  lidData?: ILidData[],
): IVesselParticularsSection[] {
  const byBay = new Map<IBayPattern, { above?: IBayLevelData; below?: IBayLevelData }>();

  baysData.forEach((bl) => {
    const entry = byBay.get(bl.isoBay) ?? {};
    if (bl.level === BayLevelEnum.ABOVE) entry.above = bl;
    else entry.below = bl;
    byBay.set(bl.isoBay, entry);
  });

  const baysUnderLids = (lidData ?? []).map((lid) => ({
    lid,
    isoBays: new Set<IBayPattern>(getIsoBaysUnderLid(lid)),
  }));

  const { belowAnchor, aboveAnchorFor } = calculateSyntheticAnchors(
    shipData.masterCGs,
    sizeSummary,
    baysData,
  );

  const sortedIsoBays = Array.from(byBay.keys()).sort();

  // The holds first: the deck's synthetic anchor sits over the highest hold stack
  const belowLevels = new Map<IBayPattern, IVesselParticularsSectionLevel>();
  sortedIsoBays.forEach((isoBay) => {
    const belowBl = byBay.get(isoBay)?.below;
    const below =
      belowBl && buildSectionLevel(belowBl, shipData.masterCGs, sizeSummary, belowAnchor);
    if (below) belowLevels.set(isoBay, below);
  });

  const holdTops = Array.from(belowLevels.values()).map((lvl) => lvl.layout.maxBB);
  const aboveAnchor = aboveAnchorFor(holdTops.length ? Math.max(...holdTops) : undefined);

  const sectionsBySignature = new Map<string, IVesselParticularsSection>();

  sortedIsoBays.forEach((isoBay) => {
    const aboveBl = byBay.get(isoBay)?.above;

    const above =
      aboveBl && buildSectionLevel(aboveBl, shipData.masterCGs, sizeSummary, aboveAnchor);
    const below = belowLevels.get(isoBay);
    if (!above && !below) return;

    const lids = baysUnderLids.filter((l) => l.isoBays.has(isoBay)).map((l) => l.lid);

    // The lids are part of the signature: bays with equal geometry but
    // different hatch covers must not share a drawn section.
    const signature = [levelSignature(above), levelSignature(below), lidsSignature(lids)].join("#");

    const existing = sectionsBySignature.get(signature);
    if (existing) {
      existing.isoBays.push(isoBay);
      return;
    }

    const levels = [above, below].filter(Boolean) as IVesselParticularsSectionLevel[];

    const rows = levels
      .flatMap((lvl) => lvl.layout.rows.map((r) => r.row))
      .filter((row, idx, arr) => arr.indexOf(row) === idx)
      .sort(sortRowsArray);

    const rowXsMm: Partial<Record<IRowPattern, number>> = {};
    const rowTcgsMm: Partial<Record<IRowPattern, number>> = {};
    rows.forEach((row) => {
      const fromLevel = levels.find((lvl) => lvl.layout.rows.some((r) => r.row === row));
      rowXsMm[row] = fromLevel?.layout.rows.find((r) => r.row === row)?.tcg;

      // The TCG is only displayable when its source level has real TCGs
      const fromRealLevel = levels.find(
        (lvl) => lvl.tcgsAreReal && lvl.layout.rows.some((r) => r.row === row),
      );
      const realTcg = fromRealLevel?.layout.rows.find((r) => r.row === row)?.tcg;
      if (realTcg !== undefined) rowTcgsMm[row] = realTcg;
    });

    sectionsBySignature.set(signature, {
      isoBays: [isoBay],
      above,
      below,
      rows,
      rowXsMm,
      rowTcgsMm,
      xMinMm: Math.min(
        ...levels.map((lvl) => Math.min(...lvl.layout.rows.map((r) => r.prevTcgMid))),
      ),
      xMaxMm: Math.max(
        ...levels.map((lvl) => Math.max(...lvl.layout.rows.map((r) => r.nextTcgMid))),
      ),
      minVcgMm: Math.min(...levels.map((lvl) => lvl.layout.minBB)),
      maxVcgMm: Math.max(...levels.map((lvl) => lvl.layout.maxBB)),
      lids,
    });
  });

  return Array.from(sectionsBySignature.values());
}

/**
 * The anchors of the synthesized bottom bases, so that a CG-less hold and deck
 * never overlap on the drawing: the hold starts at a fixed base (or, when the deck
 * has real bases, just under the lowest of them) and the deck starts just over the
 * highest hold stack (real or synthesized).
 */
function calculateSyntheticAnchors(
  masterCGs: IShipData["masterCGs"],
  sizeSummary: ISizeSummary,
  baysData: IBayLevelData[],
): {
  belowAnchor: ISyntheticAnchor;
  /** The deck anchor, given the top (mm) of the highest hold stack of the vessel, if any */
  aboveAnchorFor: (holdTopMm: number | undefined) => ISyntheticAnchor;
} {
  // Vessel-wide tier ranges per level kind
  const tiers = {
    above: { min: Infinity, max: -Infinity },
    below: { min: Infinity, max: -Infinity },
  };
  // Lowest real deck bottom base of the vessel, when some deck level has real bases
  let realAboveMinBaseMm = Infinity;

  baysData.forEach((bl) => {
    const slotKeys = usableSlotKeys(bl, sizeSummary);
    if (slotKeys.length === 0) return;

    const kind = bl.level === BayLevelEnum.ABOVE ? "above" : "below";
    slotKeys.forEach((key) => {
      const tier = Number(key.substring(2));
      if (tier < tiers[kind].min) tiers[kind].min = tier;
      if (tier > tiers[kind].max) tiers[kind].max = tier;
    });

    if (bl.level === BayLevelEnum.ABOVE) {
      const { rows, tiersByRow } = getRowsAndTiersFromSlotKeys(slotKeys);
      const bases = rows.map((row) => resolveRowBottomBase(bl, row, tiersByRow, masterCGs));
      if (bases.every((b) => b !== undefined)) {
        realAboveMinBaseMm = Math.min(realAboveMinBaseMm, ...(bases as number[]));
      }
    }
  });

  const belowTiersCount = Number.isFinite(tiers.below.min)
    ? (tiers.below.max - tiers.below.min) / 2 + 1
    : 0;

  // A synthesized hold under a real deck hangs from the deck's lowest base
  const belowBaseMm = Number.isFinite(realAboveMinBaseMm)
    ? realAboveMinBaseMm - SYNTHETIC_HATCH_CLEARANCE_MM - belowTiersCount * CONTAINER_HEIGHT_IN_MMM
    : SYNTHETIC_BELOW_BASE_MM;

  return {
    belowAnchor: { refTier: tiers.below.min, baseMm: belowBaseMm },
    aboveAnchorFor: (holdTopMm) => ({
      refTier: tiers.above.min,
      baseMm:
        holdTopMm !== undefined
          ? holdTopMm + SYNTHETIC_HATCH_CLEARANCE_MM
          : SYNTHETIC_ABOVE_BASE_MM,
    }),
  };
}

/** What makes two bays' hatch covers interchangeable on the drawing */
function lidsSignature(lids: ILidData[]): string {
  return lids
    .map(
      (lid) =>
        `${lid.label}:${lid.portIsoRow}:${lid.starboardIsoRow}:${lid.overlapPort ?? 0}:${
          lid.overlapStarboard ?? 0
        }`,
    )
    .sort()
    .join("|");
}

/** Geometry signature of a bay-level, used to group identical bays into one drawn section */
function levelSignature(lvl: IVesselParticularsSectionLevel | undefined): string {
  if (!lvl) return "-";
  return (
    `${lvl.tcgsAreReal ? "T" : "t"}${lvl.bottomBasesAreReal ? "B" : "b"}` +
    lvl.layout.rows
      .map(
        (r) =>
          `${r.row}:${roundDec(r.tcg, 0)}:${roundDec(r.bottomBaseMm, 0)}:${r.bottomTier}:${r.numTiers}:${
            r.maxHeightMm !== undefined ? roundDec(r.maxHeightMm, 0) : "-"
          }`,
      )
      .join("|")
  );
}

function buildBayCounts(baysData: IBayLevelData[]): IVesselParticularsBayCounts[] {
  const { pairedBays } = getAllThePairedBays(baysData, false);

  const byBay = new Map<IBayPattern, IVesselParticularsBayCounts>();

  const getEntry = (isoBay: IBayPattern): IVesselParticularsBayCounts => {
    const existing = byBay.get(isoBay);
    if (existing) return existing;
    const entry: IVesselParticularsBayCounts = { isoBay, count20Above: 0, count20Below: 0 };
    byBay.set(isoBay, entry);
    return entry;
  };

  baysData.forEach((bl) => {
    const entry = getEntry(bl.isoBay);
    const count20 = countSlots(bl, (sizes) => sizes.some((s) => s <= 24));
    const lcg20 = bl.infoByContLength?.[20]?.lcg;

    if (bl.level === BayLevelEnum.ABOVE) {
      entry.count20Above = count20;
      entry.lcg20AboveMm = lcg20;
    } else {
      entry.count20Below = count20;
      entry.lcg20BelowMm = lcg20;
    }
  });

  const blsByBayAndLevel = new Map<string, IBayLevelData>();
  baysData.forEach((bl) => blsByBayAndLevel.set(`${bl.isoBay}-${bl.level}`, bl));

  pairedBays.forEach((pair) => {
    const baseBl = blsByBayAndLevel.get(`${pair.base}-${pair.level}`);
    const pairedBl = blsByBayAndLevel.get(`${pair.paired}-${pair.level}`);

    const count40 = countPaired40Slots(baseBl, pairedBl);
    const lcg40 = baseBl?.infoByContLength?.[40]?.lcg ?? pairedBl?.infoByContLength?.[40]?.lcg;

    // The pair is reported once, on its base (AFT) bay
    const entry = getEntry(pair.base);
    entry.pairedWith = pair.paired;
    if (pair.level === BayLevelEnum.ABOVE) entry.count40Above = count40;
    else entry.count40Below = count40;
    if (entry.lcg40Mm === undefined) entry.lcg40Mm = lcg40;
  });

  return Array.from(byBay.values()).sort((a, b) => a.isoBay.localeCompare(b.isoBay));
}

function countSlots(bl: IBayLevelData, sizesMatch: (sizes: number[]) => boolean): number {
  const perSlotInfo = bl.perSlotInfo ?? {};
  return (Object.keys(perSlotInfo) as ISlotPattern[]).reduce((acc, key) => {
    const slot = perSlotInfo[key];
    if (!slot || slot.restricted) return acc;
    return sizesMatch(Object.keys(slot.sizes ?? {}).map(Number)) ? acc + 1 : acc;
  }, 0);
}

/** Number of 40'-capable positions of a pair: union of the two bays' slot keys, counted once */
function countPaired40Slots(baseBl?: IBayLevelData, pairedBl?: IBayLevelData): number {
  const slotKeys = new Set<ISlotPattern>([
    ...(Object.keys(baseBl?.perSlotInfo ?? {}) as ISlotPattern[]),
    ...(Object.keys(pairedBl?.perSlotInfo ?? {}) as ISlotPattern[]),
  ]);

  let count = 0;
  slotKeys.forEach((key) => {
    const has40 = [baseBl?.perSlotInfo?.[key], pairedBl?.perSlotInfo?.[key]].some(
      (slot) =>
        slot &&
        !slot.restricted &&
        Object.keys(slot.sizes ?? {})
          .map(Number)
          .some((s) => s >= 40),
    );
    if (has40) count += 1;
  });

  return count;
}

function buildParticulars(shipData: IShipData, baysData: IBayLevelData[]): IVesselMainParticulars {
  const above = baysData.filter((bl) => bl.level === BayLevelEnum.ABOVE);
  const below = baysData.filter((bl) => bl.level === BayLevelEnum.BELOW);

  return {
    shipName: shipData.shipName,
    lineOperator: shipData.lineOperator,
    callSign: shipData.callSign,
    imoCode: shipData.imoCode,
    yearBuilt: shipData.yearBuilt,
    loaMm: shipData.loa,
    lppMm: shipData.lcgOptions?.lpp,
    teuOnDeck: calculateTeusFromSlots(above),
    teuInHold: calculateTeusFromSlots(below),
    teuTotal: calculateTeusFromSlots(baysData),
  };
}
